import axios from "axios";
import { Branch } from "../components/constants/branchData";
import { useQuery } from "@tanstack/react-query";
import { companyMockData } from "../components/templates/design1/About1";
import { getClientDomain } from "./useFetchUnits";
import { transformRawBranches } from "../components/constants/helperClient";
import { logEnvironment } from "@components/lib/utils";

export type RawBranchLite = {
  id: number;
  company_id: number;
  branch_name: string;
  picture: string;
  state: string;
  local_government: string;
  city: string;
  branch_desc: string;
  branch_address: string;
  email: string | null;
  is_active: number;
  manager: {
    id: number;
    name: string;
    picture: string;
  };
  created_at: string;
  updated_at: string;
  staffs_count: number;
  properties_count: number;
};

type Pagination = {
  current_page: number;
  per_page: number;
  total_pages: number;
};

type ApiResponse = {
  status: string;
  pagination: Pagination;
  branch: RawBranchLite[];
  total_data_count: number;
  message: string;
};

export const fetchBranches = async (
  serverDomain?: string
): Promise<Branch[]> => {
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  if (isDemo) {
    return companyMockData.branches;
  }
  const allBranches: RawBranchLite[] = [];
  const domain = serverDomain || getClientDomain("fetch branches");
  logEnvironment("this the fetch branches componennt");
  const currentPage = 1;
  const url = `${process.env.NEXT_PUBLIC_BASE_URL}api/v1/custom/company-branch/${domain}`;
  try {
    // Recursive function to handle pagination
    const fetchPage = async (page: number) => {
      const response = await axios.get<ApiResponse>(`${url}?page=${page}`);
      if (response.data.status !== "success") {
        throw new Error(response.data?.message || "Failed to fetch branches");
      }
      allBranches.push(...response.data.branch);
      // If there are more pages, fetch the next page srecursively
      if (
        response.data.pagination.current_page <
        response.data.pagination.total_pages
      ) {
        await fetchPage(page + 1);
      }
    };

    // Start fetching from page 1
    await fetchPage(currentPage);
    // Transform API data to match Branch type
    const transformedBranches = transformRawBranches(allBranches);
    return transformedBranches;
  } catch (error) {
    console.error("Error fetching branches:", error);

    // Detailed error handling
    if (axios.isAxiosError(error)) {
      if (error.response) {
        console.error(
          "API Response Error:",
          error.response.status,
          error.response.data
        );
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Request setup error:", error.message);
      }
    }
    throw error;
  }
};

export const useBranches = () => {
  return useQuery<Branch[]>({
    queryKey: ["branches"],
    queryFn: () => fetchBranches(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};
